// Storage Engine Local-First para o ZAC PWA (Substitui Supabase para uso pessoal)
import { Profile, DailyAccountability, Workout } from '../types/database.types';
import { INITIAL_PROFILE, SEED_WORKOUTS, INITIAL_ACCOUNTABILITY } from '../data/mockOrSeedData';

const KEYS = {
  PROFILE: 'zac_profile',
  WORKOUTS: 'zac_workouts',
  WATER_TODAY: 'zac_water_today_ml',
  LAST_ACTIVE_DATE: 'zac_last_active_date',
  WORKOUT_DONE_TODAY: 'zac_workout_done_today',
  ACCOUNTABILITY_HISTORY: 'zac_accountability_history',
};

export function getTodayDateString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function loadLocalProfile(): Profile {
  const saved = localStorage.getItem(KEYS.PROFILE);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Sincroniza meta para 85kg e reseta ofensiva mockada de 7 dias
      if (parsed.streak_days === 7 || parsed.target_weight_kg !== 85.00) {
        const updated = {
          ...parsed,
          streak_days: parsed.streak_days === 7 ? 0 : parsed.streak_days,
          target_weight_kg: 85.00,
        };
        saveLocalProfile(updated);
        return updated;
      }
      return parsed;
    } catch (e) {
      console.error('Erro ao ler perfil salvo:', e);
    }
  }
  return INITIAL_PROFILE;
}

export function saveLocalProfile(profile: Profile): void {
  localStorage.setItem(KEYS.PROFILE, JSON.stringify(profile));
}

export function loadLocalWorkouts(): Workout[] {
  const saved = localStorage.getItem(KEYS.WORKOUTS);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Valida se os treinos salvos contêm as novas propriedades de dia da semana e cardio
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].cardio_duration_min !== undefined) {
        // Sincroniza demo_url com SEED_WORKOUTS para garantir imagens coerentes e offline
        let hasChanges = false;
        const seedMap = new Map<string, string>();
        SEED_WORKOUTS.forEach((w) => {
          w.exercises?.forEach((ex) => {
            if (ex.demo_url) seedMap.set(ex.id, ex.demo_url);
          });
        });

        parsed.forEach((w: Workout) => {
          w.exercises?.forEach((ex: any) => {
            const upToDateDemo = seedMap.get(ex.id);
            if (upToDateDemo && ex.demo_url !== upToDateDemo) {
              ex.demo_url = upToDateDemo;
              hasChanges = true;
            }
          });
        });

        if (hasChanges) {
          saveLocalWorkouts(parsed);
        }
        return parsed;
      }
    } catch (e) {
      console.error('Erro ao ler treinos salvos:', e);
    }
  }
  // Se não existe ou está desatualizado, inicializa com a ficha completa 6 dias + domingo
  saveLocalWorkouts(SEED_WORKOUTS);
  return SEED_WORKOUTS;
}

export function saveLocalWorkouts(workouts: Workout[]): void {
  localStorage.setItem(KEYS.WORKOUTS, JSON.stringify(workouts));
}

/**
 * Inicializa e verifica virada de dia para zerar água e atualizar streak
 */
export function checkDayRollover(): {
  waterMl: number;
  workoutDone: boolean;
  streakDays: number;
} {
  const today = getTodayDateString();
  const lastDate = localStorage.getItem(KEYS.LAST_ACTIVE_DATE);
  const profile = loadLocalProfile();

  if (lastDate !== today) {
    // Virada de dia!
    const previousWater = Number(localStorage.getItem(KEYS.WATER_TODAY) || 0);
    const previousWorkoutDone = localStorage.getItem(KEYS.WORKOUT_DONE_TODAY) === 'true';

    // Se houve atividade ontem e bateu a meta, incrementa streak
    let newStreak = profile.streak_days;
    if (lastDate && (previousWater >= profile.daily_water_target_ml || previousWorkoutDone)) {
      newStreak += 1;
    }

    const updatedProfile = { ...profile, streak_days: newStreak };
    saveLocalProfile(updatedProfile);

    // Reseta métricas do novo dia
    localStorage.setItem(KEYS.LAST_ACTIVE_DATE, today);
    localStorage.setItem(KEYS.WATER_TODAY, '0');
    localStorage.setItem(KEYS.WORKOUT_DONE_TODAY, 'false');

    return {
      waterMl: 0,
      workoutDone: false,
      streakDays: newStreak,
    };
  }

  return {
    waterMl: Number(localStorage.getItem(KEYS.WATER_TODAY) || 0),
    workoutDone: localStorage.getItem(KEYS.WORKOUT_DONE_TODAY) === 'true',
    streakDays: profile.streak_days,
  };
}

export function saveTodayWater(amountMl: number): void {
  localStorage.setItem(KEYS.WATER_TODAY, String(amountMl));
}

export function saveTodayWorkoutDone(done: boolean): void {
  localStorage.setItem(KEYS.WORKOUT_DONE_TODAY, String(done));
}
